import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { realLinkProps } from '../components/links/realLinkProps';
import { useRouterNavigate } from '../components/links/useEntityLinkProps';
import { useTranslation } from 'react-i18next';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  Tooltip,
} from '@fluentui/react-components';
import {
  AddRegular,
  EditRegular,
  ProhibitedRegular,
  ArrowLeftRegular,
  LocationRegular,
} from '@fluentui/react-icons';
import { useObligationsByClient, useDeactivateObligation, useRetryObligationFormHistory, ApiError } from '../services/complianceApi';
import AddObligationDialog from '../components/compliance/AddObligationDialog';
import SurtaxEditDialog from '../components/compliance/SurtaxEditDialog';
import ObligationSurtaxLine from '../components/compliance/ObligationSurtaxLine';
import { isCountySurtaxObligation } from '../utils/surtaxEdit';
import type { ComplianceObligation } from '../types';
import { apiErrorMessage } from '../lib/apiErrorMessage';
import { pageShell } from '../components/layout/pageShell';
import { cadenceName } from '../utils/cadenceName';

const useStyles = makeStyles({
  page: {
    ...pageShell,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  titleGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  title: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  emptyState: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  actionCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  backButton: {
    alignSelf: 'flex-start',
  },
});

export default function ClientObligationsPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const linkTo = useRouterNavigate();
  const styles = useStyles();
  const { t } = useTranslation();

  const resolvedClientId = clientId ?? '';

  const { data: obligations, isLoading, isError, error } = useObligationsByClient(resolvedClientId);
  const deactivateMutation = useDeactivateObligation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ComplianceObligation | undefined>(undefined);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  // STX-AUDIT-01 (Christian, 2026-11-02): a saved edit whose "Form changed" history entry wasn't recorded is SAID, with a retry, until
  // the entry is written. `outcome` is the last retry's result: 'recorded' (shown briefly as success), 'stillNot' or 'stale'.
  const [historyMissing, setHistoryMissing] = useState<
    { obligationId: string; clientId: string; before: string | null; after: string | null } | null
  >(null);
  const [historyOutcome, setHistoryOutcome] = useState<'recorded' | 'stillNot' | 'stale' | null>(null);
  const retryHistory = useRetryObligationFormHistory();
  async function handleRetryHistory() {
    if (!historyMissing) return;
    try {
      await retryHistory.mutateAsync({
        id: historyMissing.obligationId,
        clientId: historyMissing.clientId,
        entry: { before: historyMissing.before, after: historyMissing.after },
      });
      setHistoryMissing(null);
      setHistoryOutcome('recorded');
    } catch (err) {
      setHistoryOutcome(err instanceof ApiError && err.code === 'stale' ? 'stale' : 'stillNot');
    }
  }
  const formLabel = (f: string | null) => f || t('obligationsPage.formNotConfigured');
  // These rows ARE the Cosmos obligations generation mints filings from, so the row's own id is
  // the right allocation key here — unlike the client-detail card, which must use
  // filingObligationId.
  const [surtaxTarget, setSurtaxTarget] = useState<ComplianceObligation | null>(null);

  function openAdd() {
    setEditTarget(undefined);
    setDialogOpen(true);
  }

  function openEdit(obl: ComplianceObligation) {
    setEditTarget(obl);
    setDialogOpen(true);
  }

  async function handleDeactivate(obl: ComplianceObligation) {
    setDeactivateError(null);
    try {
      await deactivateMutation.mutateAsync({ id: obl.id, clientId: resolvedClientId });
    } catch (err) {
      setDeactivateError(err instanceof Error ? err.message : t('obligationsPage.deactivateFailed'));
    }
  }

  const clientName = obligations?.[0]?.clientName ?? resolvedClientId;
  const dash = t('common.dash');

  return (
    <div className={styles.page}>
      <Button
        as="a"
        appearance="subtle"
        icon={<ArrowLeftRegular />}
        className={styles.backButton}
        {...realLinkProps(`/clients/${resolvedClientId}`, { navigate: linkTo })}
      >
        {t('common.backToClient')}
      </Button>

      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <Text className={styles.title}>{t('obligationsPage.title')}</Text>
          <Text className={styles.subtitle}>{clientName}</Text>
        </div>
        <Button appearance="primary" icon={<AddRegular />} onClick={openAdd}>
          {t('obligationsPage.addObligation')}
        </Button>
      </div>

      {deactivateError && (
        <MessageBar intent="error">
          <MessageBarBody>{deactivateError}</MessageBarBody>
        </MessageBar>
      )}

      {historyMissing && (
        <MessageBar intent="warning" data-testid="history-not-recorded">
          <MessageBarBody>
            <MessageBarTitle>{t('obligationsPage.historyNotRecorded.title')}</MessageBarTitle>
            {t('obligationsPage.historyNotRecorded.body', { before: formLabel(historyMissing.before), after: formLabel(historyMissing.after) })}
            {historyOutcome === 'stillNot' && <> {t('obligationsPage.historyNotRecorded.stillNot')}</>}
            {historyOutcome === 'stale' && <> {t('obligationsPage.historyNotRecorded.stale')}</>}
          </MessageBarBody>
          <MessageBarActions>
            {historyOutcome === 'stale' ? (
              <Button size="small" onClick={() => { setHistoryMissing(null); setHistoryOutcome(null); }}>
                {t('obligationsPage.historyNotRecorded.dismiss')}
              </Button>
            ) : (
              <Button size="small" appearance="primary" disabled={retryHistory.isPending} onClick={() => { void handleRetryHistory(); }}>
                {retryHistory.isPending ? t('obligationsPage.historyNotRecorded.retrying') : t('obligationsPage.historyNotRecorded.retry')}
              </Button>
            )}
          </MessageBarActions>
        </MessageBar>
      )}
      {!historyMissing && historyOutcome === 'recorded' && (
        <MessageBar intent="success" data-testid="history-recorded">
          <MessageBarBody>{t('obligationsPage.historyNotRecorded.recorded')}</MessageBarBody>
        </MessageBar>
      )}

      {isLoading && <Spinner label={t('obligationsPage.loading')} />}

      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>
            {apiErrorMessage(error, t, 'obligationsPage.loadError')}
          </MessageBarBody>
        </MessageBar>
      )}

      {!isLoading && !isError && obligations && obligations.length === 0 && (
        <div className={styles.emptyState}>
          <Text>{t('obligationsPage.emptyState')}</Text>
        </div>
      )}

      {!isLoading && !isError && obligations && obligations.length > 0 && (
        <div className={styles.tableWrapper}>
          <Table aria-label={t('obligationsPage.title')}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('obligationsPage.columns.state')}</TableHeaderCell>
                <TableHeaderCell>{t('obligationsPage.columns.typeDisplayName')}</TableHeaderCell>
                <TableHeaderCell>{t('obligationsPage.columns.frequency')}</TableHeaderCell>
                <TableHeaderCell>{t('obligationsPage.columns.informationSource')}</TableHeaderCell>
                <TableHeaderCell>{t('obligationsPage.columns.accessOfInformation')}</TableHeaderCell>
                <TableHeaderCell>{t('obligationsPage.columns.dueOffset')}</TableHeaderCell>
                <TableHeaderCell>{t('obligationsPage.columns.formNumber')}</TableHeaderCell>
                <TableHeaderCell>{t('obligationsPage.surtaxColumn')}</TableHeaderCell>
                <TableHeaderCell>{t('obligationsPage.columns.status')}</TableHeaderCell>
                <TableHeaderCell>{t('obligationsPage.columns.actions')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {obligations.map((obl) => (
                <TableRow key={obl.id}>
                  <TableCell>
                    <TableCellLayout>{obl.state}</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    {/* Staff read the name; the taxonomy key (sales_tax) is for admins, on hover (ruled 2026-10-28). */}
                    <TableCellLayout>
                      <span title={obl.obligationType}>{obl.obligationDisplayName}</span>
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>{cadenceName(t, obl.frequency)}</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>{obl.informationSource ?? dash}</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>{obl.accessOfInformation ?? dash}</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>
                      <Tooltip
                        content={t('obligationsPage.leadTime', { days: obl.leadTimeDays })}
                        relationship="label"
                      >
                        <span>{t('obligationsPage.dayOffset', { offset: obl.dueDayOffset })}</span>
                      </Tooltip>
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    {/* BL-119: no form is "we don't know yet", never a blank that reads as "no form needed". */}
                    <TableCellLayout>{obl.formNumber || t('obligationsPage.formNotConfigured')}</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    {isCountySurtaxObligation(obl) ? (
                      <ObligationSurtaxLine clientId={resolvedClientId} obligationId={obl.id} />
                    ) : (
                      <TableCellLayout>{dash}</TableCellLayout>
                    )}
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>
                      <Badge
                        appearance="filled"
                        color={obl.isActive ? 'success' : 'subtle'}
                      >
                        {obl.isActive ? t('obligationsPage.active') : t('obligationsPage.inactive')}
                      </Badge>
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <div className={styles.actionCell}>
                      <Tooltip content={t('obligationsPage.edit')} relationship="label">
                        <Button
                          appearance="subtle"
                          icon={<EditRegular />}
                          size="small"
                          onClick={() => openEdit(obl)}
                        />
                      </Tooltip>
                      {isCountySurtaxObligation(obl) && (
                        <Tooltip content={t('obligationsPage.editSurtax')} relationship="label">
                          <Button
                            appearance="subtle"
                            icon={<LocationRegular />}
                            size="small"
                            onClick={() => setSurtaxTarget(obl)}
                          />
                        </Tooltip>
                      )}
                      {obl.isActive && (
                        <Tooltip content={t('obligationsPage.deactivate')} relationship="label">
                          <Button
                            appearance="subtle"
                            icon={<ProhibitedRegular />}
                            size="small"
                            disabled={deactivateMutation.isPending}
                            onClick={() => { void handleDeactivate(obl); }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AddObligationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        clientId={resolvedClientId}
        obligation={editTarget}
        onHistoryNotRecorded={(info) => { setHistoryMissing(info); setHistoryOutcome(null); }}
      />

      {surtaxTarget && (
        <SurtaxEditDialog
          open
          onOpenChange={(o) => { if (!o) setSurtaxTarget(null); }}
          clientId={resolvedClientId}
          obligationId={surtaxTarget.id}
          obligationLabel={`${surtaxTarget.obligationDisplayName} (${cadenceName(t, surtaxTarget.frequency)})`}
          clientObligationLabels={(obligations ?? []).map((o) => o.obligationDisplayName)}
        />
      )}
    </div>
  );
}
